<template>
  <validation-observer ref="validation" v-slot="{ invalid }" style="position: relative" tag="div">
    <m-save-profile-button :disabled="invalid || saveButtonDisabled" @onButtonClick="save" />

    <m-settings-panel
      :description="$t('profile.calendar.provider.description')"
      :title="$t('profile.calendar.provider.title')"
    >
      <v-select
        v-model="preferredCalendar"
        :items="providerOptions"
        item-text="label"
        item-value="value"
        :label="$t('profile.calendar.provider.label')"
        outlined
        dense
      />
    </m-settings-panel>

    <m-settings-panel
      :description="$t('profile.calendar.meeting.description')"
      :title="$t('profile.calendar.meeting.title')"
    >
      <v-radio-group v-model="meetingPreference" column>
        <v-radio :label="$t('profile.calendar.meeting.auto')" value="auto" />
        <v-radio :label="$t('profile.calendar.meeting.custom')" value="custom" />
        <v-radio :label="$t('profile.calendar.meeting.none')" value="none" />
      </v-radio-group>
      <validation-provider
        v-if="meetingPreference === 'custom'"
        v-slot="{ errors }"
        :rules="{ required: true, url: true }"
        :name="$t('profile.calendar.meeting.customUrlLabel')"
      >
        <v-text-field
          v-model="customMeetingUrl"
          :label="$t('profile.calendar.meeting.customUrlLabel')"
          :placeholder="$t('profile.calendar.meeting.customUrlPlaceholder')"
          :error-messages="errors"
          outlined
          dense
        />
      </validation-provider>
    </m-settings-panel>

    <m-leave-dialog :value="showDialog" @onCancel="closeDialog" @onDiscard="leavePage" @onSave="saveAndLeave" />
  </validation-observer>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import { SaveUserProfile, UsersState } from '@/store/users';
import { MSettingsPanel } from '@/components/Settings/MSettingsPanel';
import MSaveProfileButton from '../MSaveProfileButton';
import { MLeaveDialog } from '@/components/Dialogs';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

type PreferredCalendar = 'google' | 'outlook' | 'office365' | 'apple';
type MeetingPreference = 'auto' | 'custom' | 'none';

export default Vue.extend({
  name: 'm-profile-calendar',
  components: {
    MLeaveDialog,
    MSaveProfileButton,
    MSettingsPanel,
    ValidationObserver,
    ValidationProvider,
  },
  data: () => ({
    preferredCalendar: 'google' as PreferredCalendar,
    meetingPreference: 'auto' as MeetingPreference,
    customMeetingUrl: '',
    loading: false,
    showDialog: false,
    to: null,
  }),
  computed: {
    ...mapState<UsersState>('users', {
      me: (state: UsersState) => state.me,
    }),
    providerOptions() {
      return [
        { value: 'google', label: this.$t('profile.calendar.provider.google') },
        { value: 'outlook', label: this.$t('profile.calendar.provider.outlook') },
        { value: 'office365', label: this.$t('profile.calendar.provider.office365') },
        { value: 'apple', label: this.$t('profile.calendar.provider.apple') },
      ];
    },
    isChanged() {
      const a = this.me?.attributes ?? {};
      return (
        this.preferredCalendar !== (a.preferredCalendar ?? 'google') ||
        this.meetingPreference !== (a.meetingPreference ?? 'auto') ||
        this.customMeetingUrl !== (a.customMeetingUrl ?? '')
      );
    },
    saveButtonDisabled() {
      return this.loading || (!this.isChanged && this.me.complete);
    },
  },
  watch: {
    me() {
      this.updateData();
    },
  },
  beforeRouteLeave(to, from, next) {
    if (!this.isChanged || this.to) {
      next();
    } else {
      this.to = to;
      this.showDialog = true;
    }
  },
  methods: {
    updateData() {
      const a = this.me?.attributes ?? {};
      this.preferredCalendar = (a.preferredCalendar as PreferredCalendar) ?? 'google';
      this.meetingPreference = (a.meetingPreference as MeetingPreference) ?? 'auto';
      this.customMeetingUrl = a.customMeetingUrl ?? '';
    },
    async save() {
      const dto = {
        attributes: {
          preferredCalendar: this.preferredCalendar,
          meetingPreference: this.meetingPreference,
          customMeetingUrl: this.meetingPreference === 'custom' ? this.customMeetingUrl : undefined,
        },
      };
      try {
        this.loading = true;
        await this.$store.dispatch(new SaveUserProfile(dto));
      } finally {
        this.loading = false;
      }
    },
    async leavePage() {
      this.showDialog = false;
      await this.$router.push(this.to);
    },
    closeDialog() {
      this.showDialog = false;
      this.to = null;
    },
    async saveAndLeave() {
      await this.save();
      await this.leavePage();
    },
  },
  async mounted() {
    this.updateData();
  },
});
</script>
